<script setup lang="ts">
import { computed } from 'vue'
import { VAlert, VButton, VDropdownMultiple, VIcon, VToggleButtonField } from '@/modules/shared/components'
import SubscriptionCard from '../../../components/subscription-card.vue'
import AddOnCard from '../../../components/add-on-card.vue'
import type { AddOn, Subscription } from '../../../types'

const props = defineProps<{
  form: any
  v$: any
  subscriptionOptions: Subscription[]
  addOnOptions: AddOn[]
  activityOptions: { label: string; value: string }[]
  isSpvConfigDisabled: boolean
  isEditMode: boolean
  isRIA: boolean
  isPaymentOptionsAvailable: boolean
  selectedSubscription: Subscription | null
  selectedPlan: Subscription | null
  shouldShowPaymentOptions: boolean
  totalDue: any
  initialValue?: any
  isAccessible: (subscription: Subscription) => boolean
  isRecommended: (subscription: Subscription) => boolean
}>()

const isLegacyPlan = computed(
  () => props.selectedPlan != null && !props.subscriptionOptions.some((s) => s.value === props.selectedPlan?.value),
)

const emit = defineEmits<{
  (e: 'select-subscription', subscription: Subscription): void
  (e: 'select-add-on', addOn: AddOn): void
}>()

const raisingStatusOptions = [
  {
    label: 'Currently raising',
    value: 'currently_raising',
    icon: 'TrendingUp',
    description:
      'Promote the company you\u2019re raising for with investors, take commitments and track the closing status.',
  },
  {
    label: 'Closed / Not raising',
    value: 'closed',
    icon: 'CheckCircle',
    description: 'Manage distributions, reporting, and ongoing administration from your dashboard.',
  },
]

const investorRangeOptions = [
  { label: '1 - 30', value: '1-30', description: 'MicroSPV, Standard, or Premiere' },
  { label: '31 - 50', value: '31-50', description: 'Standard or Premiere' },
  { label: '51 - 99', value: '51-99', description: 'Premiere only' },
]

defineExpose({
  isValid: async () => {
    const fields = [
      'is_venture360_the_administrator',
      'is_venture360_providing_formation_documents',
      'expect_any_activity',
      'kind_of_activity',
      'raising_status',
      'investor_range',
      'is_raising_under_250k',
      'selected_services',
    ]
    const results = await Promise.all(fields.filter((f) => props.v$[f]).map((f) => props.v$[f].$validate()))
    return results.every(Boolean)
  },
})
</script>

<template>
  <div class="space-y-6">
    <VAlert v-if="isSpvConfigDisabled && initialValue" type="alert">
      This SPV has an active billing plan. View
      <RouterLink
        :to="{
          name: 'billing-entity',
          params: { billable_id: initialValue.id, billable_type: initialValue.entity_type },
        }"
        class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
      >
        Billing</RouterLink
      >. Please contact support if you need to make changes.
    </VAlert>

    <VToggleButtonField
      v-model="form.is_venture360_the_administrator"
      label="Is Venture360 the administrator of this SPV?"
      :disabled="isRIA || isSpvConfigDisabled"
      property="is_venture360_the_administrator"
      :v$="v$"
    />

    <template v-if="form.is_venture360_the_administrator">
      <VToggleButtonField
        v-model="form.is_venture360_providing_formation_documents"
        label="What kind of documents are being used to structure this SPV?"
        trueLabel="Venture360"
        falseLabel="Proprietary"
        :disabled="isRIA || isSpvConfigDisabled"
        property="is_venture360_providing_formation_documents"
        :v$="v$"
      />

      <VToggleButtonField
        v-model="form.expect_any_activity"
        label="Do you expect any activity within this SPV after the first calendar year?"
        :disabled="isRIA || isSpvConfigDisabled"
        property="expect_any_activity"
        :v$="v$"
      />

      <fieldset
        v-if="form.expect_any_activity"
        id="kind_of_activity"
        class="space-y-4 rounded-lg border border-gray-300/50 bg-white p-4"
        :disabled="isRIA || isSpvConfigDisabled"
      >
        <label class="block text-sm font-medium text-gray-800"> Which activities do you expect within this SPV? </label>
        <VDropdownMultiple
          aligned="left"
          class="z-auto"
          :options="activityOptions"
          property="kind_of_activity"
          v-model="form.kind_of_activity"
          :show-selected-list="true"
          :disabled="isRIA || isSpvConfigDisabled"
          :v$="v$"
        >
          <VButton size="xl" class="w-full">
            <div class="flex items-center justify-between">
              <div>Select multiple...</div>
              <VIcon name="ChevronSelectorVertical" />
            </div>
          </VButton>
        </VDropdownMultiple>
      </fieldset>
    </template>

    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700"> Is this SPV currently raising capital? </label>
      <div
        class="grid grid-cols-2 gap-3"
        :class="{ 'pointer-events-none opacity-50': isSpvConfigDisabled || isEditMode }"
      >
        <button
          v-for="option in raisingStatusOptions"
          :key="option.value"
          type="button"
          class="rounded-lg border-none px-5 py-4 text-left shadow-sm ring-1 transition-shadow active:translate-y-px active:shadow-sm"
          :class="
            form.raising_status === option.value
              ? 'bg-[#009192]/[0.08] text-gray-900 ring-[#009192] hover:shadow-md'
              : 'bg-white text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30'
          "
          @click="form.raising_status = option.value"
        >
          <div class="mb-1.5 flex items-center gap-2">
            <VIcon :name="option.icon" class="h-5 w-5 text-[#009192]" />
            <span class="text-sm font-semibold">{{ option.label }}</span>
          </div>
          <div class="text-[12px] leading-relaxed text-gray-400">{{ option.description }}</div>
        </button>
      </div>
      <p v-if="v$['raising_status']?.$errors?.[0]?.$message" class="mt-1 text-sm text-red-500">
        {{ v$['raising_status'].$errors[0].$message }}
      </p>
    </div>

    <template v-if="form.is_venture360_the_administrator">
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700">
          How many investors do you expect in this SPV?
        </label>
        <div class="grid grid-cols-3 gap-2" :class="{ 'pointer-events-none opacity-50': isSpvConfigDisabled }">
          <button
            v-for="option in investorRangeOptions"
            :key="option.value"
            type="button"
            class="rounded-md border-none px-3 py-3 text-left shadow-sm ring-1 transition-shadow active:translate-y-px active:shadow-sm"
            :class="
              form.investor_range === option.value
                ? 'bg-[#009192]/[0.08] text-gray-900 ring-[#009192] hover:shadow-md'
                : 'bg-white text-gray-700 ring-gray-700/20 hover:shadow-md hover:ring-gray-700/30'
            "
            @click="form.investor_range = option.value"
          >
            <div class="text-sm font-semibold leading-snug">{{ option.label }}</div>
            <div class="mt-0.5 text-[11px] text-gray-400">{{ option.description }}</div>
          </button>
        </div>
        <p v-if="v$['investor_range']?.$errors?.[0]?.$message" class="mt-1 text-sm text-red-500">
          {{ v$['investor_range'].$errors[0].$message }}
        </p>
      </div>

      <VToggleButtonField
        v-model="form.is_raising_under_250k"
        label="Are you raising less than $250k?"
        trueLabel="Yes, under $250k"
        falseLabel="No, $250k or more"
        :disabled="isSpvConfigDisabled"
        property="is_raising_under_250k"
        :v$="v$"
      />
    </template>

    <div v-if="form.is_venture360_the_administrator">
      <div id="selected_services">
        <div
          v-if="v$['selected_services']?.$errors[0]?.$message"
          class="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <VIcon name="ExclamationCircle" class="text-red-500" />
          <span>{{ v$['selected_services'].$errors[0].$message }}</span>
        </div>
      </div>

      <!-- Legacy plan: show selected plan as read-only card -->
      <template v-if="isLegacyPlan && selectedPlan">
        <SubscriptionCard
          :subscription="selectedPlan"
          :isSelected="true"
          :disabled="true"
          :selectable="false"
          :scaleOnSelect="false"
        />
      </template>

      <!-- Current plans: show plan selection -->
      <template v-else-if="shouldShowPaymentOptions">
        <div class="flex items-end gap-4">
          <template v-for="subscription in subscriptionOptions">
            <SubscriptionCard
              v-if="isAccessible(subscription)"
              :key="subscription.title"
              :subscription="subscription"
              :isRecommended="isRecommended(subscription)"
              :isSelected="selectedSubscription?.value === subscription.value"
              :disabled="isSpvConfigDisabled"
              :onClick="() => $emit('select-subscription', subscription)"
            />
          </template>
        </div>

        <div v-if="!isRIA" class="mt-8 space-y-4">
          <p class="text-sm font-medium text-gray-700">Optional add-ons to pair with your plan</p>
          <AddOnCard
            v-for="addOn in addOnOptions"
            :key="addOn.value"
            :addOn="addOn"
            :disabled="isSpvConfigDisabled"
            :isSelected="form.selected_services.includes(addOn.value)"
            :onClick="(a) => $emit('select-add-on', a)"
          />
        </div>
        <div class="mt-6 flex items-center justify-between border-t pt-4" v-if="!isSpvConfigDisabled">
          <span class="font-medium text-gray-700">Total Due</span>
          <span class="text-xl font-semibold text-gray-900">{{ totalDue }}</span>
        </div>
        <div class="text-sm text-gray-500">Payment will be setup and processed after you complete all steps.</div>
      </template>

      <!-- No plans available -->
      <div v-else-if="!isSpvConfigDisabled">
        <div class="rounded-lg border border-gray-200 bg-gray-100 p-12">
          <p class="text-center text-sm text-gray-700">
            Based on your selections, there are no available plans at this time.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
